import React from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { AppProvider, useApp } from "@/contexts/AppContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { ConsumerAuthProvider } from "@/contexts/ConsumerAuthContext";
import { AgeGate } from "@/components/AgeGate";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CompareTray } from "@/components/CompareTray";
import { InstallPrompt } from "@/components/InstallPrompt";
import { PromoNotifier } from "@/components/PromoNotifier";
import { AnnouncementBanner } from "@/components/AnnouncementBanner";
import { UsernamePrompt } from "@/components/UsernamePrompt";
import { ContactFloater } from "@/components/ContactFloater";
import { trackPageView } from "@/lib/analytics";

import Home from "@/pages/Home";
import Search from "@/pages/Search";
import StrainList from "@/pages/StrainList";
import StrainDetail from "@/pages/StrainDetail";
import DispensaryList from "@/pages/DispensaryList";
import DispensaryDetail from "@/pages/DispensaryDetail";
import MapPage from "@/pages/MapPage";
import Pricing from "@/pages/Pricing";
import DispensarySignup from "@/pages/DispensarySignup";
import DispensaryLogin from "@/pages/DispensaryLogin";
import DispensaryDashboard from "@/pages/DispensaryDashboard";
import BillingReturn from "@/pages/BillingReturn";
import Account from "@/pages/Account";
import AuthCallback from "@/pages/AuthCallback";
import AdminConsole from "@/pages/AdminConsole";
import ClaimPage from "@/pages/Claim";
import OnboardingGuide from "@/pages/OnboardingGuide";
import SaleDetail from "@/pages/SaleDetail";
import PitchDeck from "@/pages/PitchDeck";
import Legal from "@/pages/Legal";

const Shell = () => {
  const { ageVerified } = useApp();
  const location = useLocation();
  // Reset scroll position to the top whenever the route path changes.
  // Without this, navigating from a deep-scrolled search results page into
  // a strain / dispensary detail page leaves you scrolled down on the new
  // page (the strain "empty state" hero gets stranded above the fold on
  // mobile). We intentionally ignore hash/query changes so anchor jumps
  // and filter toggles don't get clobbered.
  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }
    // Operator-funnel page-view tracking. Dedupe + query-strip happens
    // inside trackPageView; this is just the trigger on every nav.
    trackPageView(location.pathname);
  }, [location.pathname]);
  // Handle OAuth redirect: /auth/callback#session_id=...
  // Synchronous check during render so we beat the auth bootstrap effect.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Chrome-less printable routes (sales-deck PDF, etc.) — no age gate, no
  // header/footer, full page bleed for clean PDF export.
  if (location.pathname === "/pitch-deck") {
    return <PitchDeck />;
  }
  return (
    <>
      <AgeGate />
      <div
        className="min-h-screen flex flex-col"
        style={{ filter: ageVerified ? "none" : "blur(2px)" }}
      >
        <SiteHeader />
        <AnnouncementBanner />
        <PromoNotifier />
        <div className="flex-1">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/search" element={<Search />} />
            <Route path="/strains" element={<StrainList />} />
            <Route path="/strains/:slug" element={<StrainDetail />} />
            <Route path="/dispensaries" element={<DispensaryList />} />
            <Route path="/dispensaries/:slug" element={<DispensaryDetail />} />
            <Route path="/map" element={<MapPage />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/sales/:id" element={<SaleDetail />} />
            <Route path="/dispensary/signup" element={<DispensarySignup />} />
            <Route path="/dispensary/login" element={<DispensaryLogin />} />
            <Route
              path="/dispensary/dashboard"
              element={<DispensaryDashboard />}
            />
            <Route path="/billing/return" element={<BillingReturn />} />
            <Route path="/account" element={<Account />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/admin" element={<AdminConsole />} />
            <Route path="/claim" element={<ClaimPage />} />
            <Route path="/onboarding" element={<OnboardingGuide />} />
            <Route path="/terms" element={<Legal slug="terms" />} />
            <Route path="/privacy" element={<Legal slug="privacy" />} />
            <Route path="/21-plus" element={<Legal slug="age" />} />
          </Routes>
        </div>
        <SiteFooter />
        <CompareTray />
        <InstallPrompt />
        <UsernamePrompt />
        <ContactFloater />
      </div>
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppProvider>
          <ConsumerAuthProvider>
            <CompareProvider>
              <Shell />
              <Toaster position="top-center" />
            </CompareProvider>
          </ConsumerAuthProvider>
        </AppProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
